<template>
  <nav
    class="bg-surface lg:bg-surface-container-low ease-slow-spatial duration-650ms fixed top-0 left-0 h-screen shrink-0 rounded-r-xl pt-11 transition-all lg:relative lg:rounded-none lg:pt-0"
    :class="[!isExpanded && 'w-0 -translate-x-1 lg:w-[96px] lg:translate-x-0']"
    :style="[isExpanded ? { width: navContentWidth ? navContentWidth + 'px' : 'auto' } : {}]"
  >
    <ul
      ref="nav-content"
      class="flex w-full flex-col items-start [&>li]:px-5"
      :class="[isExpanded && 'min-w-[272px]']"
    >
      <li
        v-if="hasModule(ApplicationModule.LUMIA)"
        class="hidden w-full shrink-0 pt-4 transition-all lg:flex"
        :class="[isExpanded && 'ml-4', !isExpanded && '-mx-2']"
      >
        <LumiaLogo class="h-auto w-20! shrink-0" />
      </li>
      <li v-else class="hidden w-full pt-4 lg:flex">
        <MenonLogo class="h-10 w-10" />
      </li>
      <li
        v-if="availableContexts.length > 1"
        class="ease-slow-spatial mt-8 w-full transition-all duration-650 md:mt-14"
        :class="[
          itemType == 'hidden' && '-translate-x-16 opacity-0 lg:delay-[0ms]',
          itemType != 'hidden' && 'translate-x-0 opacity-100 delay-150 lg:delay-[0ms]',
        ]"
      >
        <BaseConnectedButtonGroup
          v-if="isExpanded || isMobile"
          color="filled"
          :type="availableContexts.length > 2 ? 'mixed' : 'text'"
          size="XS"
          :full-width="true"
          :items="
            availableContexts.map((context) => ({
              id: context.id,
              value: context.id,
              label: context.title,
              icon: availableContexts.length == 2 ? undefined : context.icon,
              shrink: availableContexts.length > 2,
            }))
          "
          v-model="selectedContextId"
        />
        <div v-else class="flex w-full items-center justify-center">
          <BaseMenu
            class="mx-auto"
            :items="
              availableContexts.map((context) => ({
                id: context.id,
                title: context.title,
                icon: context.icon,
                leadingIcon: context.id == selectedContextId ? 'check' : undefined,
                trailingIcon: context.icon,
                callback: () => {
                  selectedContextId = context.id
                },
              }))
            "
            v-slot="{ open }"
            ><BaseIconButton
              shape="square"
              width="wide"
              color="filled"
              size="XS"
              :icon="selectedContext.icon"
              :force-pressed="open"
              v-tooltip="selectedContext.title"
          /></BaseMenu>
        </div>
      </li>
      <li
        v-if="contextCreateMenuItems.length > 0"
        class="ease-slow-spatial mb-3 transition-all duration-650"
        :class="[
          itemType == 'hidden' && '-translate-x-16 opacity-0 lg:delay-[0ms]',
          itemType != 'hidden' && 'translate-x-0 opacity-100 delay-150 lg:delay-[0ms]',
          availableContexts.length <= 1 && 'mt-16',
          availableContexts.length > 1 && 'mt-6',
        ]"
      >
        <MenuComponent :items="contextCreateMenuItems">
          <Transition
            enter-from-class="scale-0"
            enter-to-class="scale-100"
            enter-active-class="ease-fast-spatial duration-300 origin-bottom-left"
          >
            <BaseFAB
              :key="selectedContextId + 'create-menu-fab'"
              color="tertiary"
              icon="add"
              :size="isExpanded ? 'extended' : 'default'"
              no-shadows
              >{{ selectedContext.fabLabel }}</BaseFAB
            >
          </Transition>
        </MenuComponent>
      </li>
      <li
        class="relative flex max-h-[calc(100vh-12rem)] w-full flex-col overflow-x-hidden overflow-y-auto pb-4 md:max-h-[calc(100vh-13rem)]"
        :class="[
          contextCreateMenuItems.length === 0 && availableContexts.length <= 1 && 'mt-16',
          contextCreateMenuItems.length === 0 && availableContexts.length > 1 && 'mt-6',
        ]"
      >
        <Transition
          enter-from-class="opacity-0 -translate-x-16"
          enter-to-class="opacity-100 translate-x-0 relative"
          leave-from-class="opacity-100 translate-x-0"
          leave-to-class="opacity-0 translate-x-16"
          enter-active-class="ease-slow-spatial duration-500 delay-100"
          leave-active-class="ease-fast-spatial duration-300 absolute w-[calc(100%-2.5rem)] h-full"
        >
          <div :key="selectedContextId + 'context-navigation-items'" class="flex flex-col gap-4">
            <RailItemGroup
              v-for="group in contextNavigationItems"
              :key="group.id"
              :id="group.id"
              :item-type="itemType"
              :items="group.items"
              :title="group.title"
              :scrollable="group.id == 'coachees'"
              @nav-item-clicked="itemClicked"
            />
          </div>
        </Transition>
      </li>
    </ul>
    <GiveFeedbackDialog
      :is-open="isOpenGiveFeedbackDialog"
      @open-dialog="openGiveFeedbackDialog"
      @close-dialog="closeGiveFeedbackDialog"
    />
    <RequestFeedbackDialog
      :is-open="isOpenRequestFeedbackDialog"
      @close-dialog="closeRequestFeedbackDialog"
    />
    <StartSelfFeedbackDialog
      :is-open="isOpenStartSelfFeedbackDialog"
      @close-dialog="closeStartSelfFeedbackDialog"
    />
    <StartSurveyDialog
      :is-open="isOpenStartSurveyForCoacheesDialog"
      @close-dialog="closeStartSurveyForCoacheesDialog"
      @survey-created="
        (surveyId) => {
          closeStartSurveyForCoacheesDialog()
          router.push({ name: 'coach-survey', params: { id: surveyId } })
        }
      "
    />
    <StartAssessmentDialog
      v-if="hasModule(ApplicationModule.ASSESSMENT)"
      :is-open="isOpenStartAssessmentDialog"
      @close-dialog="closeStartAssessmentDialog"
      @assessment-created="
        (assessmentId) => {
          closeStartAssessmentDialog()
          router.push({ name: 'assessment', params: { surveyTemplateId: assessmentId } })
        }
      "
    />
    <CreateCoachFeedbackGoalDialog
      :is-open="isOpenCreateFeedbackGoalDialog"
      @close-dialog="closeCreateFeedbackGoalDialog"
      @goal-created="
        (goalId) => {
          closeCreateFeedbackGoalDialog()
          router.push({ name: 'coach-feedback-goal', params: { goalId } })
        }
      "
    />
  </nav>
</template>

<script setup lang="ts">
  import { useDialog } from '../base/dialog/use-dialog'
  import { useElementBounding, useWindowSize } from '@vueuse/core'
  import MenuComponent from '../base/menu/BaseMenu.vue'
  import { computed, useTemplateRef, watch } from 'vue'
  import BaseFAB from '../base/button/fab/BaseFAB.vue'
  import MenonLogo from '../MenonLogo.vue'
  import StartSelfFeedbackDialog from '@/modules/start-self-feedback/StartSelfFeedbackDialog.vue'
  import RequestFeedbackDialog from '@/modules/request-feedback/RequestFeedbackDialog.vue'
  import GiveFeedbackDialog from '@/modules/start-give-feedback/GiveFeedbackDialog.vue'
  import RailItemGroup from './RailItemGroup.vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useNavigation } from '../../composables/use-navigation'
  import type { MenuItemGroup, MenuItemType, SingleMenuItem } from '../base/menu/MenuItem'
  import { Permission } from '../../models/user'
  import { usePermission } from '../../composables/use-permission'
  import { useI18n } from 'vue-i18n'
  import BaseConnectedButtonGroup from '../base/button/BaseConnectedButtonGroup.vue'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import StartSurveyDialog from '@/modules/start-survey/components/StartSurveyDialog.vue'
  import CreateCoachFeedbackGoalDialog from '@/modules/coach-feedback-goals/components/CreateCoachFeedbackGoalDialog.vue'
  import StartAssessmentDialog from '@/modules/start-assessment/components/StartAssessmentDialog.vue'
  import { useApplicationModule } from '@/core/composables/use-application-module'
  import { ApplicationModule } from '@/core/models/organization'
  import { iconForPerspective } from '@/core/models/feedback-perspective.ts'
  import { SELF_PERSPECTIVE_KEY } from '@/core/models/perspective.ts'
  import LumiaLogo from '../LumiaLogo.vue'

  interface Props {
    isExpanded: boolean
  }

  const props = defineProps<Props>()
  const { hasModule } = useApplicationModule()

  const { width } = useWindowSize()
  const isMobile = computed(() => width.value <= 1024)
  const itemType = computed(() => {
    if (isMobile.value) {
      if (props.isExpanded) {
        return 'expanded'
      } else {
        return 'hidden'
      }
    }
    if (props.isExpanded) {
      return 'expanded'
    }
    return 'collapsed'
  })

  const navContent = useTemplateRef<HTMLAnchorElement>('nav-content')
  const navContentWidth = useElementBounding(navContent).width

  const {
    navigationItems,
    availableContexts,
    selectedContextId,
    selectedContext,
    hasJournalTemplates,
    hasEvaluationTemplates,
  } = useNavigation()

  const contextNavigationItems = computed(() =>
    navigationItems.value.filter((group) =>
      selectedContext.value.navigationGroupIds.includes(group.id),
    ),
  )

  const emits = defineEmits<{
    (e: 'nav-item-clicked'): void
  }>()

  const itemClicked = () => {
    if (isMobile.value) {
      emits('nav-item-clicked')
    }
  }

  const {
    isOpen: isOpenGiveFeedbackDialog,
    openDialog: openGiveFeedbackDialog,
    closeDialog: closeGiveFeedbackDialog,
  } = useDialog()

  const {
    isOpen: isOpenRequestFeedbackDialog,
    openDialog: openRequestFeedbackDialog,
    closeDialog: closeRequestFeedback,
  } = useDialog()

  const closeRequestFeedbackDialog = () => {
    closeRequestFeedback()
    removeQueryParam(['templateId', 'requestDialog'])
  }

  const {
    isOpen: isOpenStartSelfFeedbackDialog,
    openDialog: openStartSelfFeedbackDialog,
    closeDialog: closeStartSelfFeedbackDialog,
  } = useDialog()

  const {
    isOpen: isOpenStartSurveyForCoacheesDialog,
    openDialog: openStartSurveyForCoacheesDialog,
    closeDialog: closeStartSurveyForCoachees,
  } = useDialog()

  const {
    isOpen: isOpenStartAssessmentDialog,
    openDialog: openStartAssessmentDialog,
    closeDialog: closeStartAssessment,
  } = useDialog()

  const closeStartAssessmentDialog = () => {
    closeStartAssessment()
    removeQueryParam(['startAssessment'])
  }

  const closeStartSurveyForCoacheesDialog = () => {
    closeStartSurveyForCoachees()
    removeQueryParam(['startCoachSurvey'])
  }

  const {
    isOpen: isOpenCreateFeedbackGoalDialog,
    openDialog: openCreateFeedbackGoalDialog,
    closeDialog: closeCreateFeedbackGoalDialog,
  } = useDialog()

  const route = useRoute()
  const router = useRouter()

  watch(
    () => route.query,
    () => {
      if (route.query.requestDialog) {
        openRequestFeedbackDialog()
      }
      if (route.query.startCoachSurvey) {
        openStartSurveyForCoacheesDialog()
      }
      if (route.query.startAssessment) {
        openStartAssessmentDialog()
      }
    },
    { immediate: true },
  )

  const removeQueryParam = (param: string | string[]) => {
    const newQuery = { ...route.query }

    if (Array.isArray(param)) {
      for (const p of param) {
        if (route.query[p]) {
          delete newQuery[p]
        }
      }
    } else {
      if (route.query[param]) {
        delete newQuery[param]
      }
    }

    router.replace({ query: newQuery })
  }

  const { can } = usePermission()
  const { t } = useI18n()

  const createMenuActions = computed(() => {
    const menuItems: (SingleMenuItem & { id: string })[] = []

    if (can(Permission.FEEDBACK_GIVE) && !hasModule(ApplicationModule.LUMIA)) {
      menuItems.push({
        id: 'give-feedback',
        title: t('common.giveFeedback'),
        leadingIcon: 'send',
        callback: () => {
          if (isMobile.value) {
            emits('nav-item-clicked')
          }
          openGiveFeedbackDialog()
        },
      })
    }

    if (can(Permission.FEEDBACK_INITIATE)) {
      menuItems.push({
        id: 'request-feedback',
        title: t('common.requestFeedback'),
        leadingIcon: 'live_help',
        callback: () => {
          if (isMobile.value) {
            emits('nav-item-clicked')
          }
          openRequestFeedbackDialog()
        },
      })
      menuItems.push({
        id: 'start-self-feedback',
        title: t('common.startSelfFeedback'),
        leadingIcon: iconForPerspective(SELF_PERSPECTIVE_KEY),
        callback: () => {
          if (isMobile.value) {
            emits('nav-item-clicked')
          }
          openStartSelfFeedbackDialog()
        },
      })
    }

    if (can(Permission.COACHING_START_SURVEY) && hasJournalTemplates.value) {
      menuItems.push({
        id: 'start-survey-for-coachees',
        title: t('common.startCoachSurvey'),
        leadingIcon: 'vital_signs',
        callback: () => {
          if (isMobile.value) {
            emits('nav-item-clicked')
          }
          openStartSurveyForCoacheesDialog()
        },
      })
    }

    if (can(Permission.COACHING_START_SURVEY) && hasEvaluationTemplates.value) {
      menuItems.push({
        id: 'start-assessment',
        title: t('common.startAssessment'),
        leadingIcon: 'ballot',
        callback: () => {
          if (isMobile.value) {
            emits('nav-item-clicked')
          }
          openStartAssessmentDialog()
        },
      })
    }

    if (can(Permission.COACHING_CREATE_GOAL) && hasEvaluationTemplates.value) {
      menuItems.push({
        id: 'create-feedback-goal',
        title: t('coaching.feedbackGoals.createAction'),
        leadingIcon: 'flag',
        callback: () => {
          if (isMobile.value) {
            emits('nav-item-clicked')
          }
          openCreateFeedbackGoalDialog()
        },
      })
    }

    return menuItems
  })

  const contextCreateMenuItems = computed((): MenuItemType => {
    const items = createMenuActions.value.filter((action) =>
      selectedContext.value.fabActionIds.includes(action.id),
    )

    const selfFeedbackItems = items.filter((item) => item.id === 'start-self-feedback')
    const otherItems = items.filter((item) => item.id !== 'start-self-feedback')

    const groups: MenuItemGroup[] = []
    if (otherItems.length > 0) {
      groups.push({ items: otherItems })
    }
    if (selfFeedbackItems.length > 0) {
      groups.push({ items: selfFeedbackItems })
    }

    return groups.length === 1 ? groups[0].items : groups
  })
</script>
